import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CommunityUser {
  USER_ID: number;
  PHONE: string;
  EMAIL: string;
  ROLE: string;
  CREATED_AT: string;
  createdBy: number;
}

interface CommunityUserData {
  phone: string;
  email: string;
  password: string;
  role: 'COMMITTEE' | 'VENDOR';
}

interface UseCommunityUsersReturn {
  communityUsers: CommunityUser[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  fetchCommunityUsers: (page?: number) => Promise<void>;
  createCommunityUser: (userData: CommunityUserData) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useCommunityUsers = (): UseCommunityUsersReturn => {
  const { user, token } = useAuth();
  const [communityUsers, setCommunityUsers] = useState<CommunityUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Check if user is a community admin
  const isCommunityAdmin = user && (user.role === 'COMMITTEE_ADMIN' || user.role === 'VENDOR_ADMIN');

  const fetchCommunityUsers = useCallback(async (page: number = 1) => {
    if (!token || !isCommunityAdmin) return;

    setLoading(true);
    setError(null);
    
    try {
      // For now, we'll use a mock response since the backend doesn't have a specific endpoint for community users
      // In a real implementation, you would call an API endpoint like:
      // const response = await apiService.getCommunityUsers(page, 10, token);
      
      // Mock response for demonstration
      const mockUsers: CommunityUser[] = [
        {
          USER_ID: 1,
          PHONE: '+1234567890',
          EMAIL: 'committee1@example.com',
          ROLE: 'COMMITTEE',
          CREATED_AT: '2024-01-15T10:30:00.000Z',
          createdBy: user.userId || 1,
        },
        {
          USER_ID: 2,
          PHONE: '+1234567891',
          EMAIL: 'vendor1@example.com',
          ROLE: 'VENDOR',
          CREATED_AT: '2024-01-16T10:30:00.000Z',
          createdBy: user.userId || 1,
        },
      ];

      // Filter users based on admin role
      const filteredUsers = mockUsers.filter(mockUser => {
        if (user.role === 'COMMITTEE_ADMIN') {
          return mockUser.ROLE === 'COMMITTEE';
        } else if (user.role === 'VENDOR_ADMIN') {
          return mockUser.ROLE === 'VENDOR';
        }
        return false;
      });

      setCommunityUsers(filteredUsers);
      setCurrentPage(page);
      setTotalPages(1);
      setTotalUsers(filteredUsers.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch community users';
      setError(errorMessage);
      console.error('Error fetching community users:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user, isCommunityAdmin]);

  const createCommunityUser = useCallback(async (userData: CommunityUserData) => {
    if (!token || !isCommunityAdmin) throw new Error('No authentication token or insufficient permissions');

    try {
      // Use the regular signup endpoint for creating community users
      const response = await apiService.signup({
        ...userData,
        role: userData.role,
      });
      
      if (!response.success && !response.message) {
        throw new Error(response.message || 'Failed to create community user');
      }
      
      // Refresh the list after successful creation
      await fetchCommunityUsers(currentPage);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create community user';
      setError(errorMessage);
      throw err;
    }
  }, [token, fetchCommunityUsers, currentPage, isCommunityAdmin]);

  const refreshData = useCallback(async () => {
    await fetchCommunityUsers(currentPage);
  }, [fetchCommunityUsers, currentPage]);

  // Initial load
  useEffect(() => {
    if (isCommunityAdmin) {
      fetchCommunityUsers();
    }
  }, [fetchCommunityUsers, isCommunityAdmin]);

  return {
    communityUsers,
    loading,
    error,
    currentPage,
    totalPages,
    totalUsers,
    fetchCommunityUsers,
    createCommunityUser,
    refreshData,
  };
};
